"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { normalizeTheme } from "./theme-palette";
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const BATCH = 100;
export interface Flair {
  theme: string;
}
export type FlairMap = Record<string, Flair>;
const cache = new Map<string, Flair | null>();
const pending = new Set<string>();
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let version = 0;
function notify() {
  version += 1;
  for (const fn of listeners) fn();
}
function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
function getVersion(): number {
  return version;
}
function getServerVersion(): number {
  return -1;
}
function keyOf(name: string | null | undefined): string | null {
  const k = (name ?? "").trim().toLowerCase();
  return k ? k : null;
}
export function flairFor(name: string | null | undefined): Flair | null {
  const k = keyOf(name);
  return k ? cache.get(k) ?? null : null;
}
export function primeFlair(entries: FlairMap): void {
  for (const [k, v] of Object.entries(entries)) {
    const theme = normalizeTheme(v?.theme);
    cache.set(k.toLowerCase(), theme ? {
      theme
    } : null);
  }
  notify();
}
export function forgetFlair(name: string | null | undefined): void {
  const k = keyOf(name);
  if (k) cache.delete(k);
}
async function load(keys: string[]): Promise<void> {
  const qs = keys.map(k => `u=${encodeURIComponent(k)}`).join("&");
  try {
    const res = await fetch(`${API_BASE}/api/players/flair?${qs}`);
    const data = res.ok ? (await res.json()) as FlairMap : {};
    for (const k of keys) {
      const theme = normalizeTheme(data[k]?.theme);
      cache.set(k, theme ? {
        theme
      } : null);
    }
  } catch {
    for (const k of keys) cache.set(k, null);
  }
  notify();
}
function flush() {
  flushTimer = null;
  const keys = Array.from(pending);
  pending.clear();
  for (let i = 0; i < keys.length; i += BATCH) {
    void load(keys.slice(i, i + BATCH));
  }
}
export function requestFlair(names: (string | null | undefined)[]): void {
  let added = false;
  for (const n of names) {
    const k = keyOf(n);
    if (k && !cache.has(k) && !pending.has(k)) {
      pending.add(k);
      added = true;
    }
  }
  if (added && flushTimer === null) flushTimer = setTimeout(flush, 0);
}
export function useFlair(names) {
  const $ = _c(8);
  let t0;
  if ($[0] !== names) {
    t0 = Array.from(new Set(names.map(_temp).filter(_temp2))).sort().join("\0");
    $[0] = names;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const key = t0;
  const seen = useSyncExternalStore(subscribe, getVersion, getServerVersion);
  let t1;
  let t2;
  if ($[2] !== key) {
    t1 = () => {
      requestFlair(key ? key.split("\0") : []);
    };
    t2 = [key];
    $[2] = key;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== key || $[6] !== seen) {
    bb0: {
      const out = {};
      if (seen < 0) {
        t3 = out;
        break bb0;
      }
      for (const k_0 of key ? key.split("\0") : []) {
        const f = cache.get(k_0);
        if (f) {
          out[k_0] = f;
        }
      }
      t3 = out;
    }
    $[5] = key;
    $[6] = seen;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp2(k) {
  return !!k;
}
function _temp(n) {
  return keyOf(n);
}
export function useFlairFor(name) {
  const $ = _c(2);
  let t0;
  if ($[0] !== name) {
    t0 = [name];
    $[0] = name;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const map = useFlair(t0);
  const k = keyOf(name);
  return k ? map[k] ?? null : null;
}